/**
 * Ablaufplan: Welche Schritte laufen gleichzeitig, und wie lange dauert das
 * Rezept insgesamt, wenn man Wartezeiten nutzt?
 *
 * Regeln:
 * - Ein Schritt beginnt, wenn alles davor fertig ist – außer er ist als
 *   „gleichzeitig mit dem vorigen“ markiert; dann darf er beginnen, sobald
 *   der vorige beginnen durfte.
 * - Gekocht wird von einer Person: Aktive Schritte überlappen sich nie.
 *   Passive Schritte (Gehen, Backen, Köcheln) laufen nebenher.
 */

/** Was von einem Schritt gebraucht wird. */
export interface ScheduleStep {
  readonly title: string;
  readonly durationMinutes: number | null;
  readonly passive: boolean;
  readonly parallel: boolean;
}

/** Ein Schritt im Plan. */
export interface PlannedStep {
  readonly index: number;
  readonly label: string;
  readonly start: number;
  readonly end: number;
  readonly active: boolean;
  /** Bahn in der Darstellung, 0 ist die erste. */
  readonly lane: number;
  /** Die Dauer fehlte und wurde geschätzt. */
  readonly estimated: boolean;
}

/** Ergebnis der Planung. */
export interface Schedule {
  readonly steps: readonly PlannedStep[];
  /** Gesamtdauer mit Plan. */
  readonly total: number;
  /** Gesamtdauer, wenn alles nacheinander erledigt wird. */
  readonly sequential: number;
  readonly lanes: number;
}

/** Angenommene Dauer eines Schritts ohne Angabe. */
export const ESTIMATED_STEP_MINUTES = 5;

/** Plant die Schritte eines Rezepts. */
export function planSchedule(steps: readonly ScheduleStep[]): Schedule {
  const planned: PlannedStep[] = [];
  const laneEnds: number[] = [];
  let allEnd = 0;
  let cookFree = 0;
  let previousEarliest = 0;
  let sequential = 0;

  steps.forEach((step, index) => {
    const estimated = step.durationMinutes === null || step.durationMinutes <= 0;
    const duration = estimated ? ESTIMATED_STEP_MINUTES : step.durationMinutes;
    const earliest = step.parallel && index > 0 ? previousEarliest : allEnd;
    const active = !step.passive;
    const start = active ? Math.max(earliest, cookFree) : earliest;
    const end = start + duration;
    if (active) {
      cookFree = end;
    }
    allEnd = Math.max(allEnd, end);
    previousEarliest = earliest;
    sequential += duration;

    // Erste Bahn, die zu Beginn frei ist.
    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start);
    if (lane < 0) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = end;
    }
    planned.push({
      index,
      label: step.title.trim() === '' ? `Schritt ${index + 1}` : step.title.trim(),
      start,
      end,
      active,
      lane,
      estimated,
    });
  });

  return { steps: planned, total: allEnd, sequential, lanes: laneEnds.length };
}

/** Gibt es überhaupt etwas Gleichzeitiges? */
export function hasParallelSteps(steps: readonly ScheduleStep[]): boolean {
  return steps.some((step, index) => index > 0 && step.parallel);
}
